import React, { useState } from 'react';
import { useAppSelector } from '../app/hooks';
import { selectAuth } from '../store/slices/authSlice';

interface SettingsTab {
  key: string;
  label: string;
  icon: React.ReactNode;
}

const Settings: React.FC = () => {
  const { user } = useAppSelector(selectAuth);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Form state
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '', // User type doesn't have phone property
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    messageDelivery: true,
    campaignCompletion: true,
    templateUpdates: false,
  });
  const [apiSettings, setApiSettings] = useState({
    apiKey: 'sk_test_*****************************',
    webhookUrl: '',
    enableWebhooks: false,
  });
  
  // Tabs configuration
  const tabs: SettingsTab[] = [
    {
      key: 'profile',
      label: 'Profile',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
    },
    {
      key: 'security',
      label: 'Security',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
    },
    {
      key: 'notifications',
      label: 'Notifications',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
    },
    {
      key: 'api',
      label: 'API',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
    },
  ];
  
  // Handle form changes
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfileForm({
      ...profileForm,
      [e.target.name]: e.target.value,
    });
  };
  
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value,
    });
  };
  
  const handleNotificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotificationSettings({
      ...notificationSettings,
      [e.target.name]: e.target.checked,
    });
  };
  
  const handleApiSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setApiSettings({
      ...apiSettings,
      [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    });
  };
  
  // Form submission handlers
  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Implement API call to update profile
    console.log('Profile updated:', profileForm);
    // Show success message
    alert('Profile updated successfully!');
  };
  
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Passwords do not match!');
      return;
    }
    
    // Implement API call to update password
    console.log('Password updated');
    
    // Reset form
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    
    // Show success message
    alert('Password updated successfully!');
  };
  
  const handleNotificationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Implement API call to update notification settings
    console.log('Notification settings updated:', notificationSettings);
    // Show success message
    alert('Notification settings updated successfully!');
  };
  
  const handleApiSettingsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Implement API call to update API settings
    console.log('API settings updated:', apiSettings);
    // Show success message
    alert('API settings updated successfully!');
  };
  
  const generateNewApiKey = () => {
    // In a real app, this would make an API call to generate a new key
    const newKey = 'sk_test_' + Math.random().toString(36).substring(2, 15);
    setApiSettings({
      ...apiSettings,
      apiKey: newKey,
    });
  };
  
  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
      </div>
      
      <div className="bg-white rounded-lg shadow">
        <div className="flex flex-col md:flex-row">
          {/* Settings Navigation */}
          <div className="md:w-1/4 border-b md:border-b-0 md:border-r border-gray-200">
            <nav className="p-4">
              <ul>
                {tabs.map((tab) => (
                  <li key={tab.key} className="mb-2">
                    <button
                      className={`flex items-center w-full px-4 py-2 rounded-md ${
                        activeTab === tab.key
                          ? 'bg-whatsapp-green text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.icon}
                      <span className="ml-2">{tab.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
          
          {/* Settings Content */}
          <div className="md:w-3/4 p-6">
            {/* Profile Settings */}
            {activeTab === 'profile' && (
              <div>
                <h2 className="text-xl font-semibold mb-6">Profile Settings</h2>
                <form onSubmit={handleProfileSubmit}>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                      Full Name
                    </label>
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="name"
                      name="name"
                      type="text"
                      value={profileForm.name}
                      onChange={handleProfileChange}
                      required
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                      Email Address
                    </label>
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="email"
                      name="email"
                      type="email"
                      value={profileForm.email}
                      onChange={handleProfileChange}
                      required
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phone">
                      Phone Number
                    </label>
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="phone"
                      name="phone"
                      type="tel"
                      value={profileForm.phone}
                      onChange={handleProfileChange}
                    />
                  </div>
                  
                  <div className="flex items-center justify-end">
                    <button
                      className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      type="submit"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            )}
            
            {/* Security Settings */}
            {activeTab === 'security' && (
              <div>
                <h2 className="text-xl font-semibold mb-6">Security Settings</h2>
                <form onSubmit={handlePasswordSubmit}>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="currentPassword">
                      Current Password
                    </label>
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="currentPassword"
                      name="currentPassword"
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="newPassword">
                      New Password
                    </label>
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="newPassword"
                      name="newPassword"
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="confirmPassword">
                      Confirm New Password
                    </label>
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                  </div>
                  
                  <div className="flex items-center justify-end">
                    <button
                      className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      type="submit"
                    >
                      Update Password
                    </button>
                  </div>
                </form>
              </div>
            )}
            
            {/* Notification Settings */}
            {activeTab === 'notifications' && (
              <div>
                <h2 className="text-xl font-semibold mb-6">Notification Settings</h2>
                <form onSubmit={handleNotificationSubmit}>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        id="emailNotifications"
                        name="emailNotifications"
                        type="checkbox"
                        checked={notificationSettings.emailNotifications}
                        onChange={handleNotificationChange}
                        className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                      />
                      <label htmlFor="emailNotifications" className="ml-2 block text-gray-700">
                        Receive email notifications
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="messageDelivery"
                        name="messageDelivery"
                        type="checkbox"
                        checked={notificationSettings.messageDelivery}
                        onChange={handleNotificationChange}
                        className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                      />
                      <label htmlFor="messageDelivery" className="ml-2 block text-gray-700">
                        Message delivery status updates
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="campaignCompletion"
                        name="campaignCompletion"
                        type="checkbox"
                        checked={notificationSettings.campaignCompletion}
                        onChange={handleNotificationChange}
                        className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                      />
                      <label htmlFor="campaignCompletion" className="ml-2 block text-gray-700">
                        Campaign completion notifications
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="templateUpdates"
                        name="templateUpdates"
                        type="checkbox"
                        checked={notificationSettings.templateUpdates}
                        onChange={handleNotificationChange}
                        className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                      />
                      <label htmlFor="templateUpdates" className="ml-2 block text-gray-700">
                        Template status updates
                      </label>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex items-center justify-end">
                    <button
                      className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      type="submit"
                    >
                      Save Preferences
                    </button>
                  </div>
                </form>
              </div>
            )}
            
            {/* API Settings */}
            {activeTab === 'api' && (
              <div>
                <h2 className="text-xl font-semibold mb-6">API Settings</h2>
                <form onSubmit={handleApiSettingsSubmit}>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="apiKey">
                      API Key
                    </label>
                    <div className="flex">
                      <input
                        className="shadow appearance-none border rounded-l w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="apiKey"
                        name="apiKey"
                        type="text"
                        value={apiSettings.apiKey}
                        readOnly
                      />
                      <button
                        type="button"
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-r"
                        onClick={generateNewApiKey}
                      >
                        Generate New
                      </button>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <input
                        id="enableWebhooks"
                        name="enableWebhooks"
                        type="checkbox"
                        checked={apiSettings.enableWebhooks}
                        onChange={handleApiSettingsChange}
                        className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                      />
                      <label htmlFor="enableWebhooks" className="ml-2 block text-gray-700 font-bold">
                        Enable Webhooks
                      </label>
                    </div>
                    
                    {apiSettings.enableWebhooks && (
                      <div className="mt-2">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="webhookUrl">
                          Webhook URL
                        </label>
                        <input
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          id="webhookUrl"
                          name="webhookUrl"
                          type="url"
                          value={apiSettings.webhookUrl}
                          onChange={handleApiSettingsChange}
                          placeholder="https://yourwebsite.com/webhook"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-6 flex items-center justify-end">
                    <button
                      className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      type="submit"
                    >
                      Save API Settings
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
